import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { isEmpty } from 'ramda';

import Modal from '../modal/Modal';
import InputField from '../inputField/InputField';
import DealershipDevicesSelector from '../dealershipDevicesSelector/DealershipDevicesSelector';
import ImageCheckbox from '../imageCheckbox/ImageCheckbox';
import Loader from '../loader/Loader';
import TagList from '../tagList/TagList';
import searchIconPath from '@icons/search.svg';

import { useTranslation } from 'react-i18next';
import {
    RESPONSE,
    DeviceClassMap,
    DEFAULT_SEARCH_PAGE_SIZE,
    SEARCH_DEBOUNCE_VALUE
} from '../../js/_constants';
import { isNotEmpty } from '../../js/fpUtils';
import debounce from 'debounce';

import './deviceAssignmentModal.scss';
import { PlaylistGroup, Playlist, type Dealership, type Device } from '../../api/types/index';
import { userStore } from '../../store';
import api from '../../api';

const itemIri = item => item[RESPONSE.ID];

const sameIds = (a: string[], b: string[]) => {
    if (a.length !== b.length) {
        return false;
    }

    const setA = new Set(a);
    return b.every(id => setA.has(id));
};

const mapDevicesTag = (devices: Device[]) =>
    devices.map(device => ({
        value: device[RESPONSE.ID],
        label: `${device[RESPONSE.ID].split('/').pop()} - ${device.title}`
    }));
export interface DeviceWithIsSelected extends Device {
    isSelected: boolean;
}
export interface DealershipWithFullDevices extends Omit<Dealership, 'devices'> {
    devices: DeviceWithIsSelected[];
}

type DeviceAssignmentModalProps = {
    isForPlaylistGroup: boolean;
    playlist: Playlist | PlaylistGroup;
    isSaving: boolean;
    onClose: () => void;
    onCancel: () => void;
    onAction: () => void;
    onSelect: (devicesToSet: string[]) => void;
    defaultValue: Device[];
    playlistModuleTypesTitles?: string[];
};

const DeviceAssignmentModal = ({
    isForPlaylistGroup,
    playlist,
    isSaving,
    onClose,
    onCancel,
    onAction,
    onSelect,
    defaultValue,
    playlistModuleTypesTitles
}: DeviceAssignmentModalProps) => {
    const { t } = useTranslation();
    const [isSearchingDevices, setIsSearchingDevices] = useState(false);
    const controllerRef = useRef<AbortController | null>(null);
    const { details } = userStore();

    const [is2DChecked, setIs2DChecked] = useState(false);
    const [is3DChecked, setIs3DChecked] = useState(false);
    const [isFetchingDevices, setIsFetchingDevices] = useState(true);

    const [filteredDealerships, setFilteredDealerships] = useState<
        DealershipWithFullDevices[]
    >([]);

    const [selectedOptions, setSelectedOptions] =
        useState<DealershipWithFullDevices[]>([]);

    const [deviceTags, setDeviceTags] = useState<
        ReturnType<typeof mapDevicesTag>
    >(mapDevicesTag(defaultValue));

    const [deviceIdsToSet, setDeviceIdsToSet] = useState<string[]>(
        defaultValue.map(itemIri)
    );
    const hasUserInteractedRef = useRef(false);

    useEffect(() => {
        if (hasUserInteractedRef.current) {
            return;
        }

        const nextIds = defaultValue.map(itemIri);

        setDeviceIdsToSet(prevIds =>
            sameIds(prevIds, nextIds) ? prevIds : nextIds
        );
        setDeviceTags(mapDevicesTag(defaultValue));
    }, [defaultValue]);

    useEffect(() => {
        getDeviceSelectOptions();
    }, [playlist]);

    useEffect(() => {
        const selectedIds = new Set(deviceIdsToSet);
        const nextSelectedOptions = filteredDealerships.map(dealer => ({
            ...dealer,
            devices: dealer.devices.map(device => ({
                ...device,
                isSelected: selectedIds.has(device[RESPONSE.ID])
            }))
        }));

        setSelectedOptions(nextSelectedOptions);
    }, [filteredDealerships, deviceIdsToSet]);

     const getDeviceSelectOptions = async (query?: string) => {
            if (isSearchingDevices) {
                controllerRef.current?.abort();
            }
    
            setIsSearchingDevices(true);
    
            controllerRef.current = new AbortController();
            const signal = controllerRef.current?.signal;
            // regex to match the pattern of a valid device identifier (3 uppercase letters, 5 digits, and with or without 1 uppercase letter) for dealership search
            const regex1 = /^[A-Z]{3}\d{5}[A-Z]$/;
            const regex2 = /^[A-Z]{3}\d{5}$/;
            const match = query && (query.match(regex1) || query.match(regex2));
            // search dealers by dealershipId & deviceNumber
            try {
                let nextDealerships: any = [];
    
                const params = query ? {
                    brand: details.userGrantedBrands,
                    ...(match && query && {kvps: query}),
                    hasDevices: true,
                    pageSize: 300
                } : {
                    brand: details.userGrantedBrands,
                    hasDevices: true,
                    pageSize: 300
                };
                // force update is needed to get the latest synced devices from team Dist
                if (match) {
                    await api.devices.getDevices({
                            params: {
                                filterKvps: query,
                                force_update: 1
                            },
                            signal
                    })
                }
    
                const result = await api.user.getAllUserDealerships({
                    params,
                    signal
                });
    
                nextDealerships = result;

                let playlistModuleTypeUuids = [];
                //@ts-ignore
                !isForPlaylistGroup && (playlistModuleTypeUuids = playlist.moduleTypes.map(moduleType => moduleType.split('/').pop()));
                const devicesSearchResults = await api.devices.getAllUserDevices({
                        params: {
                            moduleType: playlistModuleTypeUuids,
                            ...(!match && query && {
                                deviceIdentifier: query,
                                title: query
                            }),
                            pageSize: 300
                        },
                        signal
                    }) as any;
                
                const dealershipsWithFullDeviceInformation = nextDealerships
                    .map(dealer => ({
                        ...dealer,
                        devices: devicesSearchResults.filter(
                            device =>
                                device.dealership ===
                                dealer['@id']
                        )
                    }))
                    // filter empty dealers: dealers that dont have devises after previous filters
                    .filter(dealer => isNotEmpty(dealer.devices));

                setFilteredDealerships(
                    dealershipsWithFullDeviceInformation as any
                );
                setIsSearchingDevices(false);
                setIsFetchingDevices(false);
            } catch (e) {
                setIsSearchingDevices(false);
                setIsFetchingDevices(false);
                return '';
            }
        };
    
  const handleSearchDevice = (query: string) => {
        getDeviceSelectOptions(query);
    };

    const debouncedHandleSearch = debounce(
        handleSearchDevice,
        SEARCH_DEBOUNCE_VALUE
    );

    const filterOptionsByClass = useCallback((baseOptions: DealershipWithFullDevices[]) => {
        if (is3DChecked) {
            const nextOptions = baseOptions.map(option => ({
                ...option,
                devices: option.devices.filter(
                    ele => ele.deviceClass === DeviceClassMap['3D']
                )
            }));

            return nextOptions.filter(option => isNotEmpty(option.devices));
        }
        if (is2DChecked) {
            const nextOptions = baseOptions.map(option => ({
                ...option,
                devices: option.devices.filter(
                    ele => ele.deviceClass === DeviceClassMap['2D']
                )
            }));

            return nextOptions.filter(option => isNotEmpty(option.devices));
        }
        return [...baseOptions];
    }, [is2DChecked, is3DChecked]);

    const dealershipsWithDeviceInfo = useMemo(
        () => filterOptionsByClass(selectedOptions),
        [filterOptionsByClass, selectedOptions]
    );

    const handelCheck2D = () => {
        setIs2DChecked(prev => {
            const nextValue = !prev;
            if (nextValue) {
                setIs3DChecked(false);
            }
            return nextValue;
        });
    };

    const handelCheck3D = () => {
        setIs3DChecked(prev => {
            const nextValue = !prev;
            if (nextValue) {
                setIs2DChecked(false);
            }
            return nextValue;
        });
    };

    const handleSelectAll = (dealerId: string) => {
        hasUserInteractedRef.current = true;
        const nextSelectedOptions = [...selectedOptions];
        const nextDeviceIds = [...deviceIdsToSet];
        const nextDeviceTags = [...deviceTags];
        const options = nextSelectedOptions.map(option => {
            if (option[RESPONSE.ID] === dealerId) {
                return {
                    ...option,
                    devices: option.devices.map(device => {
                        if (!nextDeviceIds.includes(device[RESPONSE.ID])) {
                            nextDeviceIds.push(device[RESPONSE.ID]);
                            nextDeviceTags.push({
                                value: device[RESPONSE.ID],
                                label: `${device[RESPONSE.ID].split('/').pop()} - ${device.title}`
                            });
                        }
                        return {
                            ...device,
                            isSelected: true
                        };
                    })
                };
            } else {
                return {
                    ...option
                };
            }
        });

        setSelectedOptions(options);
        setDeviceIdsToSet(nextDeviceIds);
        setDeviceTags(nextDeviceTags);
        onSelect(nextDeviceIds);
    };

    const handleDeselectAllOptions = (dealerId: string) => {
        hasUserInteractedRef.current = true;
        const nextSelectedOptions = [...selectedOptions];
        let nextDeviceIds = [...deviceIdsToSet];
        let nextDeviceTags = [...deviceTags];
        const options = nextSelectedOptions.map(option => {
            if (option[RESPONSE.ID] === dealerId) {
                return {
                    ...option,
                    devices: option.devices.map(device => {
                        nextDeviceIds = nextDeviceIds.filter(
                            val => val !== device[RESPONSE.ID]
                        );
                        nextDeviceTags = nextDeviceTags.filter(
                            val => val.value !== device[RESPONSE.ID]
                        );
                        return {
                            ...device,
                            isSelected: false
                        };
                    })
                };
            } else {
                return {
                    ...option
                };
            }
        });
        setSelectedOptions(options);
        setDeviceIdsToSet(nextDeviceIds);
        setDeviceTags(nextDeviceTags);
        onSelect(nextDeviceIds);
    };

    const handleSingleSelect = (option: DeviceWithIsSelected) => {
        hasUserInteractedRef.current = true;
        let nextDeviceIds = [...deviceIdsToSet];
        let nextDeviceTags = [...deviceTags];
        if (option.isSelected) {
            nextDeviceIds = nextDeviceIds.filter(
                val => val !== option[RESPONSE.ID]
            );
            nextDeviceTags = nextDeviceTags.filter(
                val => val.value !== option[RESPONSE.ID]
            );
        } else {
            if (!nextDeviceIds.includes(option[RESPONSE.ID])) {
                nextDeviceIds.push(option[RESPONSE.ID]);
                nextDeviceTags.push({
                    value: option[RESPONSE.ID],
                    label: `${option[RESPONSE.ID].split('/').pop()} - ${option.title}`
                });
            }
        }

        setDeviceIdsToSet(nextDeviceIds);
        setDeviceTags(nextDeviceTags);
        onSelect(nextDeviceIds);

        const nextSelectedOptions = [...selectedOptions];

        if (option.isSelected) {
            const options = nextSelectedOptions.map(ele => ({
                ...ele,
                devices: ele.devices.map(device => {
                    return {
                        ...device,
                        isSelected:
                            option[RESPONSE.ID] === device[RESPONSE.ID]
                                ? false
                                : device.isSelected
                    };
                })
            }));
            setSelectedOptions(options);
        } else {
            const options = nextSelectedOptions.map(ele => ({
                ...ele,
                devices: ele.devices.map(device => {
                    return {
                        ...device,
                        isSelected:
                            option[RESPONSE.ID] === device[RESPONSE.ID]
                                ? true
                                : device.isSelected
                    };
                })
            }));
            setSelectedOptions(options);
        }
    };

    const handleUnselectTag = value => {
        hasUserInteractedRef.current = true;
        const nextSelectedOptions = [...selectedOptions];
        let nextDeviceIds = [...deviceIdsToSet];
        let nextDeviceTags = [...deviceTags];
        nextDeviceIds = nextDeviceIds.filter(val => val !== value);
        nextDeviceTags = nextDeviceTags.filter(val => val.value !== value);
        const options = nextSelectedOptions.map(option => ({
            ...option,
            devices: option.devices.map(device => ({
                ...device,
                isSelected:
                    device[RESPONSE.ID] === value ? false : device.isSelected
            }))
        }));
        setSelectedOptions(options);
        setDeviceIdsToSet(nextDeviceIds);
        onSelect(nextDeviceIds);
        setDeviceTags(nextDeviceTags);
    };

    return (
        <Modal
            show
            onClose={onClose}
            onCancel={onCancel}
            onAction={onAction}
            isLoading={isSaving}
            className="b-assignment-modal"
        >
            <Modal.Title>
                {isForPlaylistGroup ? t('assign_playlist_group') : t('assign_playlist')} &ldquo;{playlist.title}&bdquo;{' '}
                {isForPlaylistGroup ? t('to_following_devices') : t('to_compatible_devices')}
            </Modal.Title>
            {!isForPlaylistGroup && <Modal.Header>
                <p>*{t('assign_playlist_to_device_hint_1')}:</p>
                <p>{playlistModuleTypesTitles && playlistModuleTypesTitles.length > 0 ? playlistModuleTypesTitles.join(', ') : t('no_restrictions')}</p>
                <br />
                <p style={{backgroundColor: '#e8e8e8'}}>*{t('assign_playlist_to_device_hint_2')}</p>
            </Modal.Header>}
            <div className="device-search">
                <InputField
                    onChangeHandler={(e) => debouncedHandleSearch(e.currentTarget.value)}
                    label={t('playlist_search_device')}
                    inputType="autocomplete"
                    name="device-search"
                    isFocusDisabled
                    autoFocus
                    rightIcon={<img src={searchIconPath} alt="search icon" />}
                    disabled={isFetchingDevices}
                />
            </div>
            {isNotEmpty(deviceTags) && (
                <div className="selected-options-container">
                    <TagList>
                        {deviceTags.map(option => (
                            <TagList.Tag
                                name={`selected-device-tag-${option.label}`}
                                key={option.value}
                                className="selected-option-tag"
                                title={option.label}
                                onClick={() => handleUnselectTag(option.value)}
                                noTransform
                            />
                        ))}
                    </TagList>
                </div>
            )}
            {!isForPlaylistGroup && <p className="add-alert">*{t('playlist_add_to_device_alert')}</p>}
            <div className="content-dropdown-wrapper">
                {!isEmpty(dealershipsWithDeviceInfo) && !isSearchingDevices && (
                    <div className="select-type">
                        <p>{t('device_type')}:</p>
                        <ImageCheckbox
                            className={is3DChecked ? 'is-disabled' : ''}
                            label="2D"
                            name="2d"
                            check={is2DChecked}
                            onCheck={handelCheck2D}
                        />
                        <ImageCheckbox
                            className={is2DChecked ? 'is-disabled' : ''}
                            label="3D"
                            name="3d"
                            check={is3DChecked}
                            onCheck={handelCheck3D}
                        />
                    </div>
                )}
                <ul className="device-list">
                    {isSearchingDevices ? (
                        <Loader loading isSmall />
                    ) : (
                        <div className="filtered-results">
                            {isEmpty(dealershipsWithDeviceInfo) ? (
                                <p className="no-result">
                                    {t('no_options_available')}
                                </p>
                            ) : (
                                <>
                                    {dealershipsWithDeviceInfo.map(option => (
                                        <DealershipDevicesSelector
                                            key={option[RESPONSE.ID]}
                                            dealerId={option[RESPONSE.ID]}
                                            option={option}
                                            isAllSelected={option.devices.every(
                                                device => device.isSelected
                                            )}
                                            onSelectAll={() =>
                                                handleSelectAll(
                                                    option[RESPONSE.ID]
                                                )
                                            }
                                            onDeselectAll={() =>
                                                handleDeselectAllOptions(
                                                    option[RESPONSE.ID]
                                                )
                                            }
                                            onSelect={handleSingleSelect}
                                        />
                                    ))}
                                    {dealershipsWithDeviceInfo.length >
                                        DEFAULT_SEARCH_PAGE_SIZE && (
                                        <span className="options-length-warning">
                                            Use search to narrow down options.
                                        </span>
                                    )}
                                </>
                            )}
                        </div>
                    )}
                </ul>
            </div>
        </Modal>
    );
};

export default DeviceAssignmentModal;
